// Minimal shape of the Firebase Auth instance this helper needs — kept tiny so
// it's trivially fakeable in tests.
type MinimalAuth = {
  authStateReady: () => Promise<void>;
  currentUser: { getIdToken: () => Promise<string> } | null;
};

// Build the Authorization header for a tRPC request.
export async function buildAuthHeaders(
  auth: MinimalAuth,
): Promise<{ authorization: string }> {
  // In a freshly-opened tab (e.g. the blog post preview opened via
  // target="_blank", #386) Firebase restores the persisted session
  // asynchronously, so currentUser is still null when the first request builds
  // its headers — sending no token, which the server rejects as unauthenticated.
  // Wait for the initial auth state ONLY when no user is present yet: a warm
  // authenticated request (currentUser already set) skips the wait entirely, so
  // this adds no latency to the common case or to public pages once auth has
  // initialised. authStateReady() is a local (non-network) settle of the
  // persisted state.
  if (!auth.currentUser) {
    await auth.authStateReady();
  }

  let token = "";
  try {
    token = (await auth.currentUser?.getIdToken()) || "";
  } catch {
    // A token refresh that can't complete (offline, or an expired token that
    // can't be renewed) must not reject the headers promise — that would fail
    // the request with a generic, code-less client error. Send no token instead
    // so the server replies UNAUTHORIZED and the client can surface the
    // auth-specific message (e.g. the preview's "sign in as the author").
    token = "";
  }
  return { authorization: token };
}
